import React, { useCallback, useEffect, useState } from "react";
import DistributorTable from "../../components/table/DistributorTable";
import DistributorApi from "../../utils/api/DistributorApi";
import FixedAgent from "../../utils/api/FixedAgent";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./History.module.css";
import Pagination from "../../components/list/Pagination";
import { fetchWithSearchFallback } from "../../utils/searchFallback";
import useDebouncedValue from "../../hooks/useDebouncedValue";

function SoldPins() {
  const [allTransactions, setAllTransactions] = useState(null);
  const [filteredTransactions, setFilteredTransactions] =
    useState(allTransactions);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [lastPage, setLastPage] = useState(null);
  const [serverSearchAvailable, setServerSearchAvailable] = useState(null);
  const [usingServerSearch, setUsingServerSearch] = useState(false);

  const token = useSelector((state) => state.userData.loginData.token);

  const accountType = useSelector(
    (state) => state.userData.loginData.account_type
  );

  const fetchCustomers = useCallback(async () => {
    try {
      const result = await fetchWithSearchFallback({
        search: debouncedSearch,
        serverSearchAvailable,
        fetchPage: ({ search }) => (accountType === "DISTRIBUTOR" ? DistributorApi : FixedAgent).newSoldListHistory({ token, page: currentPage, search }),
      });
      setServerSearchAvailable(result.serverSearchAvailable);
      setUsingServerSearch(result.usedServerSearch);
      const sales = result.response.data.sales;
      setAllTransactions(sales);
      setLastPage(sales?.last_page ?? null);
      setHasNextPage(!!sales?.next_page_url);
      setHasPrevPage(!!sales?.prev_page_url);
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      console.error("Failed to fetch customers", error);
    }
  }, [token, accountType, currentPage, debouncedSearch, serverSearchAvailable]);

  const clearInvoiceHandler = async (id) => {
    try {
      const response = await (accountType === "DISTRIBUTOR"
        ? DistributorApi
        : FixedAgent
      ).clearInvoice({
        token,
        id,
      });

      toast.success("Sent successfully", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      return response;
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      console.error("Failed to fetch customers", error);
    }
  };

  useEffect(() => {
    const transactions = allTransactions?.data || allTransactions;
    if (searchInput === "" || usingServerSearch) {
      setFilteredTransactions(transactions);
    } else {
      setFilteredTransactions(
        transactions?.filter((customer) =>
          customer.buyer_details?.fullname
            ?.toLowerCase()
            ?.includes(searchInput?.toLowerCase())
        )
      );
    }
  }, [searchInput, allTransactions, usingServerSearch]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return (
    <div>
      <ToastContainer />

      <div className={styles.filter_wrapper}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); setUsingServerSearch(false); setCurrentPage(1); }}
          placeholder={serverSearchAvailable === false ? "Filter this page's transactions" : "Search transactions"}
          className={styles.filter_input}
        />
        <button className={styles.export_button}>Export</button>
      </div>

      <DistributorTable
        tableData={filteredTransactions}
        table_type={"pin"}
        sold={"sold"}
        clearInvoiceHandler={clearInvoiceHandler}
      />

      <Pagination
        pagination={{ page: currentPage, lastPage: lastPage || 1, total: allTransactions?.total || 0, hasNextPage, hasPreviousPage: hasPrevPage }}
        onPageChange={setCurrentPage}
        itemLabel="sold PINs"
      />
    </div>
  );
}

export default SoldPins;

